"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Turnstile = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importStar(require("react"));
const styled_components_1 = __importDefault(require("styled-components"));
const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
function loadTurnstileScript() {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Turnstile is only available in the browser'));
    }
    if (window.turnstile) {
        return Promise.resolve(window.turnstile);
    }
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
        return new Promise((resolve, reject) => {
            const settle = () => {
                if (window.turnstile) {
                    resolve(window.turnstile);
                    return;
                }
                reject(new Error('Turnstile script loaded but API is unavailable'));
            };
            if (window.turnstile) {
                resolve(window.turnstile);
                return;
            }
            // Script may already be fully loaded (SPA remount) — `load` will not fire again.
            if (existing.getAttribute('data-loaded') === 'true') {
                settle();
                return;
            }
            existing.addEventListener('load', () => {
                existing.setAttribute('data-loaded', 'true');
                settle();
            }, { once: true });
            existing.addEventListener('error', () => reject(new Error('Failed to load Turnstile script')), {
                once: true,
            });
        });
    }
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = SCRIPT_SRC;
        // Do not use async/defer with turnstile.ready(); we wait on `onload` and call render() directly.
        script.onload = () => {
            script.setAttribute('data-loaded', 'true');
            if (window.turnstile) {
                resolve(window.turnstile);
                return;
            }
            reject(new Error('Turnstile script loaded but API is unavailable'));
        };
        script.onerror = () => reject(new Error('Failed to load Turnstile script'));
        document.head.appendChild(script);
    });
}
const TurnstileComponent = react_1.default.forwardRef(({ siteKey, action, onChange }, ref) => {
    const containerRef = (0, react_1.useRef)(null);
    const widgetIdRef = (0, react_1.useRef)(null);
    const onChangeRef = (0, react_1.useRef)(onChange);
    onChangeRef.current = onChange;
    (0, react_1.useImperativeHandle)(ref, () => ({
        reset: () => {
            var _a;
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.reset(widgetIdRef.current);
            }
            // Ant Design Form ignores `undefined` updates — use empty string to clear.
            (_a = onChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onChangeRef, '');
        },
    }));
    (0, react_1.useEffect)(() => {
        let cancelled = false;
        const renderWidget = async () => {
            if (!siteKey || !containerRef.current) {
                return;
            }
            try {
                const api = await loadTurnstileScript();
                if (cancelled || !containerRef.current || widgetIdRef.current) {
                    return;
                }
                // Call render() after onload — do not use turnstile.ready() with a dynamically injected script.
                widgetIdRef.current = api.render(containerRef.current, {
                    sitekey: siteKey,
                    action,
                    theme: 'light',
                    callback: token => {
                        var _a;
                        (_a = onChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onChangeRef, token);
                    },
                    'expired-callback': () => {
                        var _a;
                        (_a = onChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onChangeRef, '');
                    },
                    'error-callback': () => {
                        var _a;
                        (_a = onChangeRef.current) === null || _a === void 0 ? void 0 : _a.call(onChangeRef, '');
                    },
                });
            }
            catch (err) {
                // Keep the form field untouched so we don't flash validation before submit.
                // eslint-disable-next-line no-console
                console.error(err);
            }
        };
        renderWidget();
        return () => {
            cancelled = true;
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, [siteKey, action]);
    return ((0, jsx_runtime_1.jsx)(TurnstileWrapper, { children: (0, jsx_runtime_1.jsx)("div", { ref: containerRef }) }));
});
TurnstileComponent.displayName = 'Turnstile';
// Prevent re-renders when Form.Item updates `value`/`onChange` — React would otherwise
// clear the iframe Turnstile injects into the container DOM node.
const Turnstile = react_1.default.memo(TurnstileComponent, (prev, next) => prev.siteKey === next.siteKey && prev.action === next.action);
exports.Turnstile = Turnstile;
const TurnstileWrapper = styled_components_1.default.div `
	display: flex;
	justify-content: center;
	width: 100%;
`;
